import React, { useEffect, useMemo, useState } from 'react';
import { Button, Space, Input, Form, Select, Row, Col, Tag, Typography, notification, Modal } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, TeamOutlined, KeyOutlined } from '@ant-design/icons';
import { TableCustom } from '../../components/TableCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AppSelect } from '../../constants/appSelect';
import { newObjectKeys, formatDateVN } from '../../utils/utility';
import { getAllCategories } from '../../services/category';
import { deleteUser, getUsers, updateUser } from '../../services/user';

const itemName = 'Học viên';
const createItemLink = '/cms/quan-ly-hoc-vien/them-hoc-vien';
const editItemLink = '/cms/quan-ly-hoc-vien/sua-hoc-vien';
const { Text } = Typography;

const UserList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({ page: 1, limit: 20 });
  const [resetOpen, setResetOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [formReset] = Form.useForm();

  // Initialize params from URL on first load
  useEffect(() => {
    const page = Number(searchParams.get('page')) || 1;
    const limit = Number(searchParams.get('limit')) || 20;
    const keyword = searchParams.get('keyword') || undefined;
    const statusStr = searchParams.get('status');
    const statusVal = statusStr !== null ? Number(statusStr) : -1;

    const next = { page, limit };
    if (keyword) Object.assign(next, { keyword });
    if (statusVal >= 0) Object.assign(next, { status: [statusVal] });

    setParams(next);
    formSearch.setFieldsValue({ keyword, status: statusVal >= 0 ? statusVal : -1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push params to URL when they change
  useEffect(() => {
    if (!params) return;
    const sp = { page: String(params.page || 1), limit: String(params.limit || 20) };
    if (params.keyword) sp.keyword = params.keyword;
    if (Array.isArray(params.status) && params.status.length > 0 && params.status[0] >= 0) sp.status = String(params.status[0]);
    setSearchParams(sp, { replace: true });
  }, [params, setSearchParams]);

  const { data: dataUsers, isPending, refetch } = useQuery({ queryKey: ['dataUsers', params], queryFn: () => getUsers(params) });
  // fetch categories for mapping hierarchical labels in list
  const { data: allCategoriesRes } = useQuery({
    queryKey: ['allCategoriesForUserList'],
    queryFn: getAllCategories,
    staleTime: 1000 * 60 * 10,
  });
  const categoryPathLabelMap = useMemo(() => {
    const all = Array.isArray(allCategoriesRes?.data) ? allCategoriesRes.data : [];
    const cats = all.filter((c) => Number(c?.type) === 2);
    const parentMap = cats.reduce((acc, c) => { acc[c.id] = c.parent_id || 0; return acc; }, {});
    const titleMap = cats.reduce((acc, c) => { acc[c.id] = c.title || ''; return acc; }, {});
    const cache = {};
    const toPath = (id) => {
      if (cache[id]) return cache[id];
      const parts = [];
      let cur = id; const guard = new Set();
      while (cur && !guard.has(cur) && parts.length < 10) { guard.add(cur); parts.unshift(titleMap[cur] || ''); cur = parentMap[cur] || 0; }
      cache[id] = parts.filter(Boolean).join(' — ');
      return cache[id];
    };
    const map = {};
    cats.forEach((c) => { map[c.id] = toPath(c.id) || (c.title || ''); });
    return map;
  }, [allCategoriesRes]);

  const { mutate: mutateDelete } = useMutation({
    mutationFn: deleteUser,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({ message: `Xóa ${itemName} thành công!` });
        refetch();
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!' });
      }
    },
    onError: (error) => notification.error({ message: error?.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...' }),
  });

  const { mutate: mutateUpdate } = useMutation({
    mutationFn: ({ id, payload }) => updateUser(id, payload),
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({ message: 'Đặt lại mật khẩu thành công!' });
        setResetOpen(false);
        formReset.resetFields();
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!' });
      }
    },
    onError: (error) => notification.error({ message: error?.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...' }),
  });

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    if (values?.keyword) Object.assign(temp, { keyword: values?.keyword });
    if (values?.status >= 0) Object.assign(temp, { status: [values?.status] });

    if (!Object.is(JSON.stringify(temp), JSON.stringify(params))) {
      setParams(temp);
      return;
    }
    refetch();
  };

  const columns = [
    { title: 'STT', dataIndex: 'id', key: 'stt', width: 70, fixed: 'left', align: 'center', ellipsis: true, onHeaderCell: () => ({ style: { maxWidth: 70 } }), onCell: () => ({ style: { maxWidth: 70, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }), render: (_, __, index) => (params?.page - 1) * params?.limit + index + 1 },
    { title: 'Họ và tên', dataIndex: 'full_name', key: 'full_name', render: (_, { full_name, id }) => (<Link to={`${editItemLink}/${id}`} className="text-[#1677FF]">{full_name}</Link>), minWidth: 120, width: 150 },
    { title: 'Số điện thoại', dataIndex: 'phone', key: 'phone', render: (v) => <Text>{v}</Text>, minWidth: 90, width: 120 },
    { title: 'Ngày tạo', dataIndex: 'created_at', key: 'created_at', render: (created_at) => created_at ? <Text className="text-[#667085]">{formatDateVN(created_at)}</Text> : null, width: 130 },
    { title: 'Danh mục', dataIndex: 'enrollments', key: 'enrollments',
      render: (enrollments) => {
        const arr = Array.isArray(enrollments) ? enrollments : [];
        if (arr.length === 0) return <Text className="text-[#667085]">Học thử</Text>;
        return (
          <div className="space-y-1">
            {arr.map((e, i) => {
              const title = categoryPathLabelMap[e?.category_id] || `ID ${e?.category_id}`;
              const expire = e?.expire_date ? formatDateVN(e.expire_date) : 'Không thời hạn';
              return (
                <div key={i} className="text-sm text-[#344054]">
                  {title} <span className="text-[#667085]">(hết hạn: {expire})</span>
                </div>
              );
            })}
          </div>
        );
      },
      minWidth: 220,
      width: 320,
    },
    { title: 'Cập nhật lần cuối', dataIndex: 'updated_at', key: 'updated_at', render: (_, { updated_at }) => <Space direction="vertical">{updated_at && <Text className="text-[#667085]">{formatDateVN(updated_at)}</Text>}</Space> },
    { title: 'Trạng thái', dataIndex: 'status', key: 'status', render: (status) => (<Tag className="px-2 py-1" color={(AppSelect.STATUS || []).find((item) => item?.value === status)?.color}>{(AppSelect.STATUS || []).find((item) => item?.value === status)?.label}</Tag>), width: 90, fixed: 'right' },
    { title: 'Thao tác', key: 'action', render: (_, record) => (
      <Space size={0}>
        <Button type="text" onClick={() => navigate(`${editItemLink}/${record?.id}`)}>
          <EditOutlined />
        </Button>
        <Button type="text" title="Reset mật khẩu" onClick={() => { setResetTarget(record); setResetOpen(true); }}>
          <KeyOutlined />
        </Button>
        {record?.status !== 2 && (
          <Button type="text" onClick={() => mutateDelete(record?.id)}>
            <DeleteOutlined />
          </Button>
        )}
      </Space>
    ), width: 120, fixed: 'right' },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">Danh sách {itemName}</h1>
        <Link to={createItemLink}>
          <Button type="primary">
            <PlusOutlined />
            Thêm {itemName}
          </Button>
        </Link>
      </div>

      <Form name="search_users" form={formSearch} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="keyword" label={<Text>Từ khóa</Text>}>
              <Input placeholder="Nhập tên hoặc số điện thoại..." />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="status" label={<Text>Trạng thái</Text>} initialValue={-1}>
              <Select showSearch filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())} options={[{ value: -1, label: 'Tất cả' }, ...(AppSelect.STATUS || [])]} />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit"><SearchOutlined />Tìm kiếm</Button>
            {params && Object.values(newObjectKeys(params, ['keyword', 'status'])).some((value) => value) && (
              <Button htmlType="button" onClick={() => { formSearch.resetFields(); setParams({ page: 1, limit: params.limit }); }}>
                <DeleteOutlined />
                Xóa tìm kiếm
              </Button>
            )}
          </Col>
        </Row>
      </Form>

      <TableCustom columns={columns} dataSource={dataUsers?.data ?? []} totalItem={dataUsers?.total ?? 0} loading={isPending} params={params} setParams={setParams} />

      <Modal
        title={resetTarget ? `Đặt lại mật khẩu - ${resetTarget.full_name} (${resetTarget.phone})` : 'Đặt lại mật khẩu'}
        open={resetOpen}
        onCancel={() => { setResetOpen(false); formReset.resetFields(); }}
        onOk={() => formReset.submit()}
        okText="Cập nhật"
        cancelText="Hủy"
        destroyOnClose
      >
        <Form form={formReset} layout="vertical" requiredMark={false} onFinish={(vals) => {
          const pwd = vals?.password;
          if (!pwd || String(pwd).length < 6) {
            notification.warning({ message: 'Mật khẩu tối thiểu 6 ký tự' });
            return;
          }
          mutateUpdate({ id: resetTarget?.id, payload: { password: pwd } });
        }}>
          <Form.Item name="password" label={<Text>Mật khẩu mới <span className="text-red-700">*</span></Text>} rules={[{ required: true, message: 'Vui lòng nhập mật khẩu mới' }, { min: 6, message: 'Tối thiểu 6 ký tự' }]}>
            <Input.Password placeholder="Nhập mật khẩu mới..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserList;
